import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import { useExportResumeMutation, useGetProfileStrengthByEmployeeQuery } from '../resumeApiSlice';
import { businessUnits } from '../../../constants/businessUnits';
import { useParams } from 'react-router-dom';
import { variants } from '../../../variants/variants';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { selectResumeDownloadStarted } from '../resumeSlice';

const ExportResume = ({ businessUnit }) => {
  const { empCode } = useParams();
  const [exportResume, { isLoading }] = useExportResumeMutation();
  const downloadStarted = useSelector(selectResumeDownloadStarted);
  const validationSchema = Yup.object().shape({
    businessUnit: Yup.string().required('Business Unit is required'),
  });

  const [bu, setBu] = useState(businessUnit);

  useLayoutEffect(() => {
    setBu(businessUnit);
  }, [empCode]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessUnit: 'GatewayDigital',
      format: 'pdf', // Default format is PDF
    },
    validationSchema,
    onSubmit: async (values) => {
      const loadingToastId = toast.loading('Exporting Resume...');
      try {
        const { data } = await exportResume({
          empCode: empCode,
          businessUnit: values.businessUnit,
          format: values.format,
        });
        if (data) {
          const { blob, filename } = data;
          if (window['ReactNativeWebView']) {
            const reader = new FileReader();
            reader.onloadend = function () {
              const base64data = reader.result.split(',')[1];
              window['ReactNativeWebView']?.postMessage(
                JSON.stringify({ type: 'download', base64: base64data, filename })
              );
            };
            reader.readAsDataURL(blob);
            // window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'download', url: downloadUrl, filename }));
          } else {
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Resume downloaded successfully!', { id: loadingToastId });
          }
        }
      } catch (error) {
        toast.dismiss(loadingToastId);
        toast.error('Failed to export resume.');
      } finally {
        toast.dismiss(loadingToastId);
      }
    },
  });
  useEffect(() => {
    //sending the progress status to webview
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(
        JSON.stringify({
          type: 'resume',
          downloadStarted: downloadStarted,
          value: `Resume: ${empCode}-${formik.values.businessUnit}`,
        })
      );
  }, [downloadStarted]);
  return (
    <Box
      component='form'
      onSubmit={formik.handleSubmit}
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <TextField
        fullWidth
        label='Select Business Unit'
        id='businessUnit'
        name='businessUnit'
        value={formik.values.businessUnit}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.businessUnit && !!formik.errors.businessUnit}
        helperText={formik.touched.businessUnit && formik.errors.businessUnit}
        select
        variant='outlined'
      >
        {businessUnits.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <FormControl fullWidth>
        <InputLabel id='format-select-label'>Export Format</InputLabel>
        <Select
          labelId='format-select-label'
          id='format'
          name='format'
          value={formik.values.format}
          label='Export Format'
          disabled={true}
          onChange={formik.handleChange}
        >
          <MenuItem value='pdf'>PDF</MenuItem>
          <MenuItem value='doc'>DOC</MenuItem>
        </Select>
      </FormControl>
      <Button
        type='submit'
        variant='contained'
        fullWidth
        sx={{ fontWeight: 'bold', borderRadius: '8px', fontSize: variants.fontSize.TEXT }}
        disabled={isLoading || formik.isSubmitting}
      >
        Export Resume
      </Button>
    </Box>
  );
};

export default ExportResume;
